import { useMutation, useQuery } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { MessageKey } from "../../i18n/dictionaries";

// The core product lines the advisor prepare-offers boundary supports — the
// catalog-taxonomy tri-pillar spine (ADR 0004 §4), grounded to the API's
// RECOMMENDATION_LINE_CODES (packages/validation recommendation.ts): VEHICLE
// (trafik/kasko), DWELLING (dask/konut) and PERSON/HEALTH (tss/oss = sağlık,
// seyahat = travel). Kept as a local mirror because the console depends only on
// @csa/web-foundation (no @csa/validation import); the values must not drift.
export const CORE_LINE_CODES = [
  "trafik",
  "kasko",
  "dask",
  "konut",
  "tss",
  "oss",
  "seyahat",
] as const;
export type LineCode = (typeof CORE_LINE_CODES)[number];

/** i18n key for a line's Turkish label (po.line.<code>). */
export const lineLabelKey = (code: LineCode): MessageKey => `po.line.${code}` as MessageKey;

/** One advisor-entered offer line sent to the API — the PACKAGE (carrier is
 *  resolved server-side from packageCode) + its gross premium as a TRY decimal
 *  string. There is deliberately NO carrierId here (integrity posture, #167). */
export interface PrepareOfferLine {
  packageCode: string;
  grossPremium: string;
  currency?: "TRY";
}

/** POST /v1/leads/:leadId/prepare-offers request body (1..3 offers). */
export interface PrepareOffersInput {
  offers: PrepareOfferLine[];
  lineCode?: LineCode;
  insuredAssetId?: string;
  needsAssessment?: Record<string, unknown>;
}

/** The orchestration result — a scored DRAFT recommendation the advisor drives
 *  through choose → tier-2 → present at /recommendations/:recommendationId. */
export interface PrepareOffersResult {
  recommendationId: string;
  quoteRequestId: string;
  offerIds: string[];
}

/** POST /v1/leads/:leadId/prepare-offers — one call: lead → quote_request →
 *  distribution-gated offers → scored DRAFT recommendation. Advisor-gated
 *  (recommendation:write/own); the caller must own the lead. Rides the
 *  governance client, so a step-up / four-eyes challenge is handled centrally. */
export function usePrepareOffers(leadId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (input: PrepareOffersInput) =>
      api.post<PrepareOffersResult>(`/v1/leads/${leadId}/prepare-offers`, input),
  });
}

/** GET /v1/leads/:leadId/context — the offer-header glance context for the
 *  "3 Teklif Hazırla" surface: the lead party's display name + latest VEHICLE
 *  plate + phone. Each field is NULLABLE (a party may have no name parts, no
 *  vehicle line, or no phone on file — never fabricated). Same lead-ownership
 *  scope as the detail read (a lead the caller can't see is an indistinguishable
 *  404). Source of record: apps/api/src/modules/lead/lead.service.ts (LeadContext). */
export interface LeadContext {
  partyName: string | null;
  plate: string | null;
  phone: string | null;
}

/** GET /v1/leads/:leadId/context — reads the offer-header context (name/plate/
 *  phone). Preferred over the queue's nav-state hand-off (which is absent when the
 *  surface is opened directly); the caller falls back to nav-state name/plate if
 *  this read fails. lead:read/own-gated server-side. */
export function useLeadContext(leadId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["lead-context", leadId],
    queryFn: () => api.get<LeadContext>(`/v1/leads/${leadId}/context`),
    enabled: leadId !== "",
    retry: false,
  });
}

/** An ACTIVE advisor in the caller's org — the reassignment picker option
 *  (GET /v1/leads/assignable-advisors returns a bare array). */
export interface AssignableAdvisor {
  staffId: string;
  displayName: string;
}

/** GET /v1/leads/assignable-advisors — ACTIVE advisors in the org, for the
 *  "Danışmana ata / Devret" picker. lead:assign-gated server-side. */
export function useAssignableAdvisors(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["assignable-advisors"],
    queryFn: () => api.get<AssignableAdvisor[]>("/v1/leads/assignable-advisors"),
    enabled,
  });
}

/** POST /v1/leads/:leadId/assign — reassign lead ownership. Body carries the
 *  API's exact field name `ownerStaffId` (packages/validation reassignLeadSchema).
 *  lead:assign-gated + audited server-side; returns the updated lead row. */
export function useReassignLead(leadId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (ownerStaffId: string) =>
      api.post<{ ownerStaffId: string }>(`/v1/leads/${leadId}/assign`, { ownerStaffId }),
  });
}
